import { useInboxStore } from '@/entities/inbox';
import { usePhysicianStore } from '@/entities/physician';
import { EVENT_PRIORITY_DROPDOWN_OPTIONS } from '@/shared/config';
import { auth, generateFullName, GETTEXT_ALL_PRIORITY, GETTEXT_ALL_STUDIES } from '@/shared/lib';
import { useMergeState } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { t } from 'ttag';
import type { RecentInboxSearchComponentProps } from './recentInboxSearchComponent.types';

/**
 * Supplies the recent inbox search bar with its options, physician list and layout state.
 * Reads physicians and the inbox store, memoizes the search-by options (dropping Physician for a
 * clinic physician outside all studies), maps physicians into select options and tracks a narrow
 * viewport through a window resize listener.
 *
 * @param props - Search bar props carrying selectedTypeSeePatients.
 * @returns An object carrying the layout and physician-option state with its setter, the store
 *   physicians and inbox goto, and the priority and search-by options.
 */
const useRecentInboxSearchComponent = ({
  selectedTypeSeePatients,
}: Pick<RecentInboxSearchComponentProps, 'selectedTypeSeePatients'>) => {
  /**
   * Physician list read from the physician store, used to build the physician search options. Selects only the physicians field so unrelated store updates do not trigger a re-render.
   *
   * @returns The physician records held in the physician store.
   */
  const physiciansRedux = usePhysicianStore((s) => s.physicians);
  /**
   * Subscription to the whole inbox store, exposing the persisted tab and search goto state to the recent-inbox search component. Selects the entire store object rather than a single field, so any inbox store change re-renders the consumer.
   *
   * @returns The complete inbox store state and actions.
   */
  const inboxesGotoRedux = useInboxStore((s) => s);

  const priorityOptions = useRef([GETTEXT_ALL_PRIORITY, ...EVENT_PRIORITY_DROPDOWN_OPTIONS]);

  /**
   * Memoized set of search-by options offered by the inbox search bar. Drops the Physician option when the signed-in user is a clinic physician and the selected patient scope is not all studies, otherwise lists patient name, study ID, patient ID and physician.
   *
   * @returns Option objects carrying label, value and title for each searchable field.
   */
  const searchByList = useMemo(() => {
    if (auth.isClinicPhysician() && selectedTypeSeePatients?.value !== GETTEXT_ALL_STUDIES) {
      return [
        {
          label: t`Patient name`,
          value: 'patientName',
          title: 'by Patient name',
        },
        {
          label: t`Study ID`,
          value: 'studyFid',
          title: 'by Study ID',
        },
        {
          label: t`Patient ID`,
          value: 'patientId',
          title: 'by Patient ID',
        },
      ];
    }
    return [
      {
        label: t`Patient name`,
        value: 'patientName',
        title: 'by Patient name',
      },
      {
        label: t`Study ID`,
        value: 'studyFid',
        title: 'by Study ID',
      },
      {
        label: t`Patient ID`,
        value: 'patientId',
        title: 'by Patient ID',
      },
      {
        label: t`Physician`,
        value: 'physicianId',
        title: 'by Physician',
      },
    ];
  }, [selectedTypeSeePatients?.value]);
  const [state, setState] = useMergeState<any>({
    isSmallWidth: window.innerWidth < 1080,
    physicianOptions: [],
  });

  useEffect(() => {
    /**
     * Tracks a narrow viewport so the recent inbox search box can collapse. Sets isSmallWidth below 1080px and clears it again at or above that width.
     *
     * @returns Nothing.
     */
    const handleResizeWindow = () => {
      if (window.innerWidth < 1080 && !state.isSmallWidth) {
        setState({ isSmallWidth: true });
      }
      if (window.innerWidth >= 1080 && state.isSmallWidth) {
        setState({ isSmallWidth: false });
      }
    };
    window.addEventListener('resize', handleResizeWindow);
    return () => {
      window.removeEventListener('reisize', handleResizeWindow);
    };
  }, [state.isSmallWidth]);

  useEffect(() => {
    /**
     * Select options derived from the physician store list, one entry per physician. Maps each physician to a label built from its full name and a value taken from its id.
     *
     * @returns Option objects with a full-name label and the physician id as value.
     */
    const physicianOptions = _.map(physiciansRedux, (physician) => ({
      label: generateFullName(physician),
      value: physician.id,
    }));
    setState({ physicianOptions });
  }, [physiciansRedux]);

  return {
    state,
    setState,
    physiciansRedux,
    inboxesGotoRedux,
    priorityOptions,
    searchByList,
  };
};

export { useRecentInboxSearchComponent };
